import React, { useState } from "react";

function IconButton({ onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-2 bg-white border border-slate-300 hover:bg-slate-50 rounded-md text-slate-500 hover:text-slate-700 transition-colors"
    >
      {children}
    </button>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <IconButton onClick={handleCopy} title={copied ? "Copied!" : "Copy to clipboard"}>
      {copied ? (
        <svg className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
        </svg>
      )}
    </IconButton>
  );
}

export default function CredentialsDisplay({ app, variant = "created", onClose }) {
  const [showSecret, setShowSecret] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const isRotated = variant === "rotated";

  const discoveryUrl = `${window.location.protocol}//api.${window.location.host.replace(/^[^.]+\./, '')}/.well-known/openid-configuration`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center z-50 pt-16 pb-10 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">
            {isRotated ? `New Secret — ${app.appName || "App"}` : "App Credentials"}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {isRotated
              ? "The previous secret has been invalidated. Save the new one below."
              : "The client secret will not be shown again."}
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
            <strong>This is the only time the {isRotated ? "new " : ""}secret will be shown.</strong> Copy it now and store it in a password manager or deployment secret store.
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Client ID</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm">{app.client_id}</code>
              <CopyButton text={app.client_id} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Client Secret</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm break-all">
                {showSecret ? app.client_secret : "\u2022".repeat(Math.min(app.client_secret.length, 48))}
              </code>
              <IconButton onClick={() => setShowSecret(!showSecret)} title={showSecret ? "Hide secret" : "Show secret"}>
                {showSecret ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                )}
              </IconButton>
              <CopyButton text={app.client_secret} />
            </div>
          </div>

          {!isRotated && <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Setup reference</label>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-2 font-mono text-xs">
              <div><span className="text-slate-500">Discovery URL:</span><br/>{discoveryUrl}</div>
              <div><span className="text-slate-500">Token auth method:</span> client_secret_basic</div>
              <div><span className="text-slate-500">Redirect URIs:</span><br/>{(app.redirect_uris || []).join("\n")}</div>
            </div>
          </div>}

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
            <span className="text-sm text-slate-700">I have copied and stored the client secret safely.</span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button disabled={!confirmed} onClick={onClose} className="px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
